/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Advanced Filter utils for Claims
 */
import _ from 'lodash';
import { JsonTree } from '@react-awesome-query-builder/antd';
import { OPERATORS } from 'lib/SearchQueryBuilder/config/queryConfig';
import { QUERY_BUILDER_FILTER_TYPE } from 'lib/SearchQueryBuilder/config/entity.config';

import { getFilterQueryFromSearchParams } from 'utils/advancedFilterUtils';
import { CLAIM_STATUS_LIST_VIEW_FIELDS, CLAIM_TABLE_COLUMN_IDS } from 'app-constants/claims';
import { filterDataKeys } from 'utils/adfUtils';

const FieldTypeConfigs = {
	text: {
		ui_field_type: QUERY_BUILDER_FILTER_TYPE.text,
		searchOperator: OPERATORS.like,
	},
	datePicker: {
		ui_field_type: QUERY_BUILDER_FILTER_TYPE.datePicker,
		searchOperator: OPERATORS.equal,
	},
	rangePicker: {
		ui_field_type: QUERY_BUILDER_FILTER_TYPE.rangePicker,
		searchOperator: OPERATORS.between,
	},
	multiselect: {
		ui_field_type: QUERY_BUILDER_FILTER_TYPE.multiselect,
		searchOperator: OPERATORS.multiselectContains,
	},
	number: {
		ui_field_type: QUERY_BUILDER_FILTER_TYPE.number,
		searchOperator: OPERATORS.equal,
	},
	treeMultiSelect: {
		ui_field_type: QUERY_BUILDER_FILTER_TYPE.treeMultiSelect,
		searchOperator: OPERATORS.multiselectContains,
	},
	treeSelect: {
		ui_field_type: QUERY_BUILDER_FILTER_TYPE.treeSelect,
		searchOperator: OPERATORS.equal,
	},
	minMaxRange: {
		ui_field_type: QUERY_BUILDER_FILTER_TYPE.minMaxRange,
		searchOperator: OPERATORS.between,
	},
};

const SERVICE_DATE_FILTER_SCHEMA = {
	...FieldTypeConfigs.rangePicker,
	field: filterDataKeys.serviceDate,
	display_name: CLAIM_STATUS_LIST_VIEW_FIELDS.serviceDate.label,
	operators: [OPERATORS.between],
	isSearchable: true,
	dependencyColumn: CLAIM_TABLE_COLUMN_IDS.serviceDates,
	custom_props: {
		placeholder: CLAIM_STATUS_LIST_VIEW_FIELDS.serviceDate.placeholder,
	},
};

/**
 * SERVICE_DATE_QUERY_PARAMS
 */
const getFilterParamsWithServiceDate = (dateRange: [string, string]): JsonTree => {
	return getFilterQueryFromSearchParams({
		searchParams: {
			key: filterDataKeys.serviceDate,
			value: dateRange,
		},
		// TODO: payload still not fixed
		filterSchema: [{ ...SERVICE_DATE_FILTER_SCHEMA, searchOperator: OPERATORS.between }],
	});
};

const parseQueryFilterParams = ({
	query,
	field,
	parser,
}: {
	query: JsonTree | null;
	field: string;
	parser: (value: AnyType) => AnyType;
}): JsonTree | null => {
	if (_.isEmpty(query)) {
		return null;
	}

	const transformNode = (node: AnyType): AnyType => {
		if (node?.type === 'rule') {
			// Check if this rule is for the status field
			if (node?.properties?.field === field) {
				const currentValues = node.properties.value || [];
				const transformedValues = [];
				currentValues.forEach((value: AnyType) => {
					const transformedValue = parser(value);
					if (!_.isEmpty(transformedValue)) {
						transformedValues.push(transformedValue);
					}
				});

				return {
					...node,
					properties: {
						...node?.properties,
						value: transformedValues,
						operator: OPERATORS.multiselectContains,
						valueType: ['multiselect'],
					},
				};
			}
			return node;
		} else if (node?.type === 'group' && node?.children1) {
			// Recursively process all children in the group
			return {
				...node,
				children1: node?.children1?.map(transformNode),
			};
		}

		return node;
	};

	return transformNode(query);
};

export { FieldTypeConfigs, SERVICE_DATE_FILTER_SCHEMA, getFilterParamsWithServiceDate, parseQueryFilterParams, filterDataKeys };
